import {
  ArrowLeft,
  Check,
  Eye,
  RefreshCcw,
  Save,
  Send,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  loadNovaWebsiteEditorDocuments,
  publishNovaWebsiteDocument,
  saveNovaWebsiteDraft,
  type NovaWebsiteEditorDocument,
} from "../content/nova-website-content";

type EditorField = {
  key: string;
  label: string;
  type?: "text" | "textarea";
};

type WebsiteEditorPage = {
  id: string;
  number?: string;
  label: string;
  documentKeys: string[];
  global?: boolean;
};

const WEBSITE_EDITOR_PAGES: WebsiteEditorPage[] = [
  {
    id: "hero",
    number: "01",
    label: "Beginning / Hero",
    documentKeys: ["website.hero"],
  },
  {
    id: "table",
    number: "02",
    label: "Restaurant / Table",
    documentKeys: ["website.table"],
  },
  {
    id: "menu",
    number: "03",
    label: "Menu",
    documentKeys: ["website.menu"],
  },
  {
    id: "juice",
    number: "04",
    label: "Juice Station",
    documentKeys: ["website.juice"],
  },
  {
    id: "guest-book",
    number: "05",
    label: "Guest Book",
    documentKeys: [
      "website.guest_book",
      "website.reviews",
    ],
  },
  {
    id: "at-nova",
    number: "06",
    label: "At Nova",
    documentKeys: ["website.at_nova"],
  },
  {
    id: "reservation",
    number: "07",
    label: "Gather Here",
    documentKeys: ["website.reservation"],
  },
  {
    id: "location",
    number: "08",
    label: "Find Us",
    documentKeys: ["website.location"],
  },
  {
    id: "footer",
    number: "09",
    label: "Footer",
    documentKeys: ["website.footer"],
  },

  {
    id: "restaurant-details",
    label: "Restaurant Details",
    documentKeys: ["website.brand"],
    global: true,
  },
  {
    id: "chapter-navigation",
    label: "Chapter Navigation",
    documentKeys: ["website.chapters"],
    global: true,
  },
];


const FIELD_DEFINITIONS: Record<
  string,
  EditorField[]
> = {
  "website.brand": [
    { key: "city", label: "City" },
    { key: "address", label: "Address" },
    { key: "phone", label: "Phone" },
    { key: "whatsapp", label: "WhatsApp" },
    { key: "instagram", label: "Instagram handle" },

    { key: "hours1Day", label: "Opening hours — row 1 days" },
    { key: "hours1Time", label: "Opening hours — row 1 time" },

    { key: "hours2Day", label: "Opening hours — row 2 days" },
    { key: "hours2Time", label: "Opening hours — row 2 time" },

    { key: "hours3Day", label: "Opening hours — row 3 days" },
    { key: "hours3Time", label: "Opening hours — row 3 time" },
  ],

  "website.chapters": [
    { key: "beginning", label: "Beginning navigation label" },
    { key: "table", label: "Table navigation label" },
    { key: "menu", label: "Menu navigation label" },
    { key: "juice", label: "Juice navigation label" },
    { key: "reviews", label: "Guest Book navigation label" },
    { key: "shop", label: "At Nova navigation label" },
    { key: "gather", label: "Gather navigation label" },
    { key: "location", label: "Location navigation label" },
  ],

  "website.hero": [
    { key: "sectionLabel", label: "Section label" },
    { key: "titleLine1", label: "Title — line 1" },
    { key: "titleAccent", label: "Title — accent line" },
    { key: "titleLine3", label: "Title — final word" },
    { key: "copy", label: "Introduction", type: "textarea" },
    { key: "primaryCta", label: "Primary button" },
    { key: "secondaryCta", label: "Secondary button" },
    { key: "scrollHint", label: "Scroll hint" },
    { key: "imageAlt", label: "Hero image alt text" },
  ],

  "website.table": [
    { key: "sectionLabel", label: "Section label" },
    { key: "titleLine1", label: "Title — line 1" },
    { key: "titleLine2", label: "Title — line 2" },
    { key: "copy", label: "Introduction", type: "textarea" },
    { key: "cta", label: "Reservation button" },
    { key: "imageAlt", label: "Restaurant image alt text" },
    { key: "imageBadge", label: "Image badge" },
  ],

  "website.menu": [
    { key: "sectionNumber", label: "Section number" },
    { key: "marker", label: "Section marker" },
    { key: "titleLine1", label: "Title — line 1" },
    { key: "titleLine2", label: "Title — line 2" },
    { key: "intro", label: "Menu introduction", type: "textarea" },
    { key: "allTab", label: "All tab" },
    { key: "foodTab", label: "Food tab" },
    { key: "drinksTab", label: "Drinks tab" },
    { key: "categoryHint", label: "Category hint" },
    { key: "currentlyBrowsing", label: "Currently browsing label" },
    { key: "photographyLine1", label: "Photography placeholder — line 1" },
    { key: "photographyLine2", label: "Photography placeholder — line 2" },
    { key: "favouriteLabel", label: "Featured item label" },
    { key: "chooseSizeLabel", label: "Choose size message" },
    { key: "showHighlightsLabel", label: "Show highlights button" },
    { key: "showFullMenuLabel", label: "Show full menu button" },
    { key: "exploreAllPrefix", label: "Explore all prefix" },
    { key: "itemsLabel", label: "Items label" },
    { key: "footerLabel", label: "Menu footer label" },
    { key: "reviewOrderLabel", label: "Review order button" },
  ],

  "website.juice": [
    { key: "sectionLabel", label: "Section label" },
    { key: "titleLine1", label: "Title — line 1" },
    { key: "titleLine2", label: "Title — line 2" },
    { key: "copy", label: "Introduction", type: "textarea" },
    { key: "imageAlt", label: "Juice image alt text" },
    { key: "offerLabel", label: "Offer label" },
    { key: "offerCopy", label: "Offer / staple copy" },
  ],

  "website.guest_book": [
    { key: "sectionNumber", label: "Section number" },
    { key: "marker", label: "Section marker" },
    { key: "rating", label: "Displayed rating" },
    { key: "reviewCount", label: "Displayed review count" },
    { key: "reviewCountLabel", label: "Review count label" },
    { key: "openLabel", label: "Open reviews button" },
    { key: "closeLabel", label: "Close reviews button" },
  ],

  "website.at_nova": [
    { key: "sectionNumber", label: "Section number" },
    { key: "eyebrow", label: "Eyebrow" },
    { key: "titleLine1", label: "Title — line 1" },
    { key: "titleLine2", label: "Title — line 2" },
    { key: "copy", label: "Introduction", type: "textarea" },
    { key: "lead", label: "Lead paragraph", type: "textarea" },
    { key: "body", label: "Supporting paragraph", type: "textarea" },
    { key: "cta", label: "Button text" },

    { key: "reelLabel", label: "Reel card label" },
    { key: "reelNumber", label: "Reel card number" },
    { key: "reelTitleLine1", label: "Reel title — line 1" },
    { key: "reelTitleLine2", label: "Reel title — line 2" },
    { key: "reelCopy", label: "Reel description", type: "textarea" },
    { key: "reelFooter", label: "Reel footer label" },

    { key: "gameLabel", label: "Game Nights label" },
    { key: "gameTitleLine1", label: "Game Nights title — line 1" },
    { key: "gameTitleLine2", label: "Game Nights title — line 2" },
    { key: "gameCopy", label: "Game Nights copy", type: "textarea" },

    { key: "celebrationLabel", label: "Celebrations label" },
    { key: "celebrationTitleLine1", label: "Celebrations title — line 1" },
    { key: "celebrationTitleLine2", label: "Celebrations title — line 2" },
    { key: "celebrationCopy", label: "Celebrations copy", type: "textarea" },

    { key: "kitchenLabel", label: "Kitchen label" },
    { key: "kitchenCopy", label: "Kitchen copy", type: "textarea" },
    { key: "momentsLabel", label: "Nova Moments label" },
    { key: "momentsCopy", label: "Nova Moments copy", type: "textarea" },
  ],

  "website.reservation": [
    { key: "sectionNumber", label: "Section number" },
    { key: "eyebrow", label: "Eyebrow" },
    { key: "titleLine1", label: "Title — line 1" },
    { key: "titleLine2", label: "Title — line 2" },
    { key: "copy", label: "Introduction", type: "textarea" },

    { key: "callLabel", label: "Call link text" },
    { key: "whatsappLabel", label: "WhatsApp link text" },

    { key: "successEyebrow", label: "Success eyebrow" },
    { key: "successTitle", label: "Success title" },
    { key: "successCopy", label: "Success message", type: "textarea" },
    { key: "successAgain", label: "Make another reservation button" },

    { key: "formEyebrow", label: "Form eyebrow" },
    { key: "formTitle", label: "Form title" },
    { key: "nameLabel", label: "Name field label" },
    { key: "namePlaceholder", label: "Name placeholder" },
    { key: "emailLabel", label: "Email field label" },
    { key: "emailPlaceholder", label: "Email placeholder" },
    { key: "dateLabel", label: "Date field label" },
    { key: "timeLabel", label: "Time field label" },
    { key: "guestsLabel", label: "Guests field label" },
    { key: "noteLabel", label: "Note field label" },
    { key: "optionalLabel", label: "Optional label" },
    { key: "notePlaceholder", label: "Note placeholder", type: "textarea" },
    { key: "sendingLabel", label: "Sending state" },
    { key: "submitLabel", label: "Submit button" },
  ],

  "website.location": [
    { key: "sectionNumber", label: "Section number" },
    { key: "marker", label: "Section marker" },
    { key: "titleLine1", label: "Title — line 1" },
    { key: "titleLine2", label: "Title — line 2" },
    { key: "copy", label: "Location story", type: "textarea" },
    { key: "address", label: "Address" },
    { key: "locality", label: "Locality" },
    { key: "hoursLine1", label: "Opening days" },
    { key: "hoursLine2", label: "Opening hours" },
    { key: "phone", label: "Phone" },
    { key: "mapNameLine1", label: "Map card — line 1" },
    { key: "mapNameLine2", label: "Map card — line 2" },
    { key: "mapCoordinates", label: "Map caption" },
    { key: "mapUrl", label: "Google Maps URL" },
    { key: "mapCta", label: "Map button text" },
  ],

  "website.footer": [
    { key: "taglineLine1", label: "Tagline — line 1" },
    { key: "taglineLine2", label: "Tagline — line 2" },
    { key: "findUsLabel", label: "Find Us label" },
    { key: "keepInTouchLabel", label: "Keep In Touch label" },
    { key: "instagramUrl", label: "Instagram URL" },
    { key: "facebookUrl", label: "Facebook URL" },
    { key: "copyright", label: "Copyright" },
    { key: "closingLine", label: "Closing line" },
  ],
};

function WebsiteDocumentEditor({
  document,
  onChanged,
}: {
  document: NovaWebsiteEditorDocument;
  onChanged: (
    document: NovaWebsiteEditorDocument,
  ) => void;
}) {
  const fields =
    FIELD_DEFINITIONS[
      document.content_key
    ];

  if (
    document.content_key ===
    "website.reviews"
  ) {
    const items = Array.isArray(
      document.value.items,
    )
      ? (
          document.value
            .items as Array<
            Record<string, unknown>
          >
        )
      : [];

    const updateItems = (
      nextItems: Array<
        Record<string, unknown>
      >,
    ) => {
      onChanged({
        ...document,
        value: {
          ...document.value,
          items: nextItems,
        },
        hasDraft: true,
      });
    };

    const addReview = () => {
      updateItems([
        ...items,
        {
          quote: "",
          name: "",
          detail: "",
        },
      ]);
    };

    const removeReview = (
      index: number,
    ) => {
      updateItems(
        items.filter(
          (_, itemIndex) =>
            itemIndex !== index,
        ),
      );
    };

    const moveReview = (
      index: number,
      direction: -1 | 1,
    ) => {
      const target =
        index + direction;

      if (
        target < 0 ||
        target >= items.length
      ) {
        return;
      }

      const next = [...items];

      [
        next[index],
        next[target],
      ] = [
        next[target],
        next[index],
      ];

      updateItems(next);
    };

    return (
      <div className="space-y-6">
        <div className="flex flex-col justify-between gap-4 border-b border-[#b5bbae] pb-5 sm:flex-row sm:items-center">
          <div>
            <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#9d725c]">
              Public reviews
            </p>

            <p className="mt-2 text-sm leading-6 text-[#667067]">
              Add as many reviews as Nova
              needs. The website shows the
              first reviews initially and
              expands the complete collection
              when guests choose View all
              reviews.
            </p>
          </div>

          <button
            type="button"
            onClick={addReview}
            className="shrink-0 border border-[#314a3b] px-4 py-3 text-[9px] font-semibold uppercase tracking-[0.16em]"
          >
            + Add review
          </button>
        </div>

        {items.length === 0 && (
          <div className="border border-dashed border-[#b5bbae] px-5 py-8 text-center text-sm text-[#667067]">
            No public reviews yet.
          </div>
        )}

        {items.map((item, index) => (
          <div
            key={`review-${index}`}
            className="border border-[#b5bbae] bg-[#eee9dc] p-5"
          >
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#9d725c]">
                Review {index + 1}
              </p>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={index === 0}
                  onClick={() =>
                    moveReview(index, -1)
                  }
                  className="border border-[#b5bbae] px-3 py-2 text-[8px] uppercase tracking-[0.14em] disabled:opacity-30"
                >
                  Move up
                </button>

                <button
                  type="button"
                  disabled={
                    index ===
                    items.length - 1
                  }
                  onClick={() =>
                    moveReview(index, 1)
                  }
                  className="border border-[#b5bbae] px-3 py-2 text-[8px] uppercase tracking-[0.14em] disabled:opacity-30"
                >
                  Move down
                </button>

                <button
                  type="button"
                  onClick={() =>
                    removeReview(index)
                  }
                  className="border border-[#9d725c]/50 px-3 py-2 text-[8px] uppercase tracking-[0.14em] text-[#9d725c]"
                >
                  Remove
                </button>
              </div>
            </div>

            <label className="block">
              <span className="mb-2 block text-[9px] uppercase tracking-[0.17em] text-[#718076]">
                Quote
              </span>

              <textarea
                rows={4}
                value={String(
                  item.quote ?? "",
                )}
                onChange={(event) => {
                  const nextItems =
                    items.map(
                      (
                        candidate,
                        itemIndex,
                      ) =>
                        itemIndex === index
                          ? {
                              ...candidate,
                              quote:
                                event
                                  .target
                                  .value,
                            }
                          : candidate,
                    );

                  updateItems(nextItems);
                }}
                className="w-full resize-none border-b border-[#b5bbae] bg-transparent py-3 text-sm outline-none focus:border-[#9d725c]"
              />
            </label>

            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              {[
                [
                  "name",
                  "Guest name",
                ],
                [
                  "detail",
                  "Location / detail",
                ],
              ].map(
                ([key, label]) => (
                  <label
                    key={key}
                    className="block"
                  >
                    <span className="mb-2 block text-[9px] uppercase tracking-[0.17em] text-[#718076]">
                      {label}
                    </span>

                    <input
                      value={String(
                        item[key] ?? "",
                      )}
                      onChange={(
                        event,
                      ) => {
                        const nextItems =
                          items.map(
                            (
                              candidate,
                              itemIndex,
                            ) =>
                              itemIndex ===
                              index
                                ? {
                                    ...candidate,
                                    [key]:
                                      event
                                        .target
                                        .value,
                                  }
                                : candidate,
                          );

                        updateItems(
                          nextItems,
                        );
                      }}
                      className="w-full border-b border-[#b5bbae] bg-transparent py-3 text-sm outline-none focus:border-[#9d725c]"
                    />
                  </label>
                ),
              )}
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={addReview}
          className="w-full border border-dashed border-[#718076] px-5 py-4 text-[9px] font-semibold uppercase tracking-[0.17em] text-[#314a3b]"
        >
          + Add another review
        </button>
      </div>
    );
  }

  if (!fields) {
    return (
      <p className="text-sm text-[#667067]">
        This section will join the visual
        editor in the next pass.
      </p>
    );
  }

  return (
    <div className="grid gap-6">
      {fields.map((field) => {
        const value = String(
          document.value[
            field.key
          ] ?? "",
        );

        return (
          <label
            key={field.key}
            className="block"
          >
            <span className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.17em] text-[#718076]">
              {field.label}
            </span>

            {field.type ===
            "textarea" ? (
              <textarea
                rows={4}
                value={value}
                onChange={(event) =>
                  onChanged({
                    ...document,
                    value: {
                      ...document.value,
                      [field.key]:
                        event.target
                          .value,
                    },
                    hasDraft: true,
                  })
                }
                className="w-full resize-none border-b border-[#b5bbae] bg-transparent py-3 text-sm leading-6 outline-none focus:border-[#9d725c]"
              />
            ) : (
              <input
                value={value}
                onChange={(event) =>
                  onChanged({
                    ...document,
                    value: {
                      ...document.value,
                      [field.key]:
                        event.target
                          .value,
                    },
                    hasDraft: true,
                  })
                }
                className="w-full border-b border-[#b5bbae] bg-transparent py-3 text-sm outline-none focus:border-[#9d725c]"
              />
            )}
          </label>
        );
      })}
    </div>
  );
}

export function NovaCmsWebsiteEditor() {
  const [documents, setDocuments] =
    useState<
      NovaWebsiteEditorDocument[]
    >([]);
  const [activePageId, setActivePageId] =
    useState<string>("hero");
  const [loading, setLoading] =
    useState(true);
  const [busyKey, setBusyKey] =
    useState<string | null>(null);
  const [message, setMessage] =
    useState<string | null>(null);
  const [error, setError] =
    useState<string | null>(null);

  const load = useCallback(
    async () => {
      setLoading(true);
      setError(null);

      try {
        const next =
          await loadNovaWebsiteEditorDocuments();

        setDocuments(next);
} catch (loadError) {
        console.error(
          "[Nova CMS] website editor load failed",
          loadError,
        );

        setError(
          loadError instanceof Error
            ? loadError.message
            : "Website content could not be loaded.",
        );
      }

      setLoading(false);
    },
    [],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const activePage =
    useMemo(
      () =>
        WEBSITE_EDITOR_PAGES.find(
          (page) =>
            page.id ===
            activePageId,
        ) ??
        WEBSITE_EDITOR_PAGES[0],
      [activePageId],
    );

  const activeDocuments =
    useMemo(
      () =>
        activePage.documentKeys
          .map((contentKey) =>
            documents.find(
              (document) =>
                document.content_key ===
                contentKey,
            ),
          )
          .filter(
            (
              document,
            ): document is NovaWebsiteEditorDocument =>
              Boolean(document),
          ),
      [activePage, documents],
    );

  async function save(
    document: NovaWebsiteEditorDocument,
  ) {
    setBusyKey(
      document.content_key,
    );
    setMessage(null);
    setError(null);

    try {
      await saveNovaWebsiteDraft(
        document,
      );

      setMessage(
        `${document.label} draft saved.`,
      );

      await load();
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : "Draft could not be saved.",
      );
    }

    setBusyKey(null);
  }

  async function publish(
    document: NovaWebsiteEditorDocument,
  ) {
    setBusyKey(
      document.content_key,
    );
    setMessage(null);
    setError(null);

    try {
      await saveNovaWebsiteDraft(
        document,
      );

      await publishNovaWebsiteDocument(
        document.content_key,
      );

      setMessage(
        `${document.label} is now live.`,
      );

      await load();
    } catch (publishError) {
      setError(
        publishError instanceof Error
          ? publishError.message
          : "Content could not be published.",
      );
    }

    setBusyKey(null);
  }

  return (
    <main className="min-h-screen bg-[#eee9dc] text-[#314a3b]">
      <header className="border-b border-[#53685b] bg-[#314a3b] text-[#f3ead7]">
        <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-6 px-5 py-5 sm:px-8">
          <button
            type="button"
            onClick={() => {
              window.location.href =
                "/nova-hq";
            }}
            className="flex items-center gap-3 text-[9px] font-semibold uppercase tracking-[0.18em]"
          >
            <ArrowLeft size={14} />
            Nova HQ
          </button>

          <div className="flex items-center gap-5">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 text-[9px] font-semibold uppercase tracking-[0.16em] text-[#d6bd91]"
            >
              <Eye size={14} />
              View live website
            </a>

            <img
              src="/images/nova/logodark.png"
              onError={(event) => {
                event.currentTarget.src =
                  "/images/nova/novatransparent.png";
              }}
              alt="Nova Res Culina"
              className="h-14 w-auto object-contain"
            />
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-[1500px] px-5 py-10 sm:px-8 lg:py-14">
        <div className="flex flex-col justify-between gap-7 border-b border-[#b5bbae] pb-9 lg:flex-row lg:items-end">
          <div>
            <p className="text-[9px] font-semibold uppercase tracking-[0.24em] text-[#9d725c]">
              Website
            </p>

            <h1 className="mt-4 font-['Cormorant_Garamond'] text-5xl leading-none sm:text-6xl">
              Edit what Nova says.
            </h1>

            <p className="mt-5 max-w-2xl text-sm leading-7 text-[#667067]">
              Change the exact words and
              punctuation without changing
              the approved website design.
              Save a draft first, then
              publish when it is ready.
            </p>
          </div>

          <button
            type="button"
            onClick={() =>
              void load()
            }
            disabled={loading}
            className="flex h-11 items-center gap-3 border border-[#9d725c] px-4 text-[9px] font-semibold uppercase tracking-[0.16em] text-[#9d725c] disabled:opacity-50"
          >
            <RefreshCcw size={14} />
            Refresh
          </button>
        </div>

        {message && (
          <div className="mt-6 flex items-center gap-3 border border-[#718076]/30 bg-[#dfe3d7] px-5 py-4 text-xs">
            <Check size={15} />
            {message}
          </div>
        )}

        {error && (
          <div className="mt-6 border border-[#9d725c]/30 bg-[#9d725c]/8 px-5 py-4 text-xs text-[#7d5544]">
            {error}
          </div>
        )}

        {loading &&
        documents.length === 0 ? (
          <p className="mt-10 text-[10px] uppercase tracking-[0.2em] text-[#718076]">
            Loading website editor…
          </p>
        ) : (
          <div className="mt-8 grid gap-7 lg:grid-cols-[285px_1fr]">
            <nav className="self-start border border-[#b5bbae] bg-[#f4efe4] p-3 lg:sticky lg:top-6">
              <p className="px-4 pb-3 pt-2 text-[8px] font-semibold uppercase tracking-[0.2em] text-[#9d725c]">
                Website pages
              </p>

              {WEBSITE_EDITOR_PAGES.filter(
                (page) => !page.global,
              ).map((page) => {
                const hasDraft =
                  page.documentKeys.some(
                    (key) =>
                      documents.find(
                        (document) =>
                          document.content_key ===
                          key,
                      )?.hasDraft,
                  );

                return (
                  <button
                    key={page.id}
                    type="button"
                    onClick={() =>
                      setActivePageId(
                        page.id,
                      )
                    }
                    className={[
                      "flex w-full items-center gap-3 px-4 py-4 text-left",
                      activePageId ===
                      page.id
                        ? "bg-[#314a3b] text-[#f3ead7]"
                        : "text-[#667067] hover:bg-[#eee9dc]",
                    ].join(" ")}
                  >
                    <span
                      className={[
                        "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-[8px]",
                        activePageId ===
                        page.id
                          ? "border-[#d6bd91]/50 text-[#d6bd91]"
                          : "border-[#b5bbae] text-[#9d725c]",
                      ].join(" ")}
                    >
                      {page.number}
                    </span>

                    <span>
                      <span className="block text-[9px] font-semibold uppercase tracking-[0.14em]">
                        {page.label}
                      </span>

                      {hasDraft && (
                        <span className="mt-1 block text-[8px] uppercase tracking-[0.14em] text-[#c79b7c]">
                          Draft changes
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}

              <div className="my-4 border-t border-[#b5bbae]" />

              <p className="px-4 pb-3 text-[8px] font-semibold uppercase tracking-[0.2em] text-[#9d725c]">
                Global settings
              </p>

              {WEBSITE_EDITOR_PAGES.filter(
                (page) => page.global,
              ).map((page) => {
                const hasDraft =
                  page.documentKeys.some(
                    (key) =>
                      documents.find(
                        (document) =>
                          document.content_key ===
                          key,
                      )?.hasDraft,
                  );

                return (
                  <button
                    key={page.id}
                    type="button"
                    onClick={() =>
                      setActivePageId(
                        page.id,
                      )
                    }
                    className={[
                      "flex w-full items-center justify-between gap-3 px-4 py-4 text-left",
                      activePageId ===
                      page.id
                        ? "bg-[#314a3b] text-[#f3ead7]"
                        : "text-[#667067] hover:bg-[#eee9dc]",
                    ].join(" ")}
                  >
                    <span>
                      <span className="block text-[9px] font-semibold uppercase tracking-[0.14em]">
                        {page.label}
                      </span>

                      {hasDraft && (
                        <span className="mt-1 block text-[8px] uppercase tracking-[0.14em] text-[#c79b7c]">
                          Draft changes
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </nav>

            <div className="space-y-6">
              <div className="border border-[#b5bbae] bg-[#f4efe4] px-6 py-5 sm:px-9">
                <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-[#9d725c]">
                  {activePage.global
                    ? "Global settings"
                    : `Website chapter ${activePage.number}`}
                </p>

                <h2 className="mt-2 font-['Cormorant_Garamond'] text-4xl">
                  {activePage.label}
                </h2>
              </div>

              {activeDocuments.map(
                (activeDocument) => (
                  <article
                    key={
                      activeDocument.content_key
                    }
                    className="border border-[#b5bbae] bg-[#f4efe4] p-6 sm:p-9"
                  >
                    <div className="mb-8 flex flex-col justify-between gap-5 border-b border-[#b5bbae] pb-6 sm:flex-row sm:items-end">
                      <div>
                        <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-[#9d725c]">
                          {
                            activeDocument.section_key
                          }
                        </p>

                        <h3 className="mt-3 font-['Cormorant_Garamond'] text-3xl">
                          {
                            activeDocument.label
                          }
                        </h3>
                      </div>

                      <div className="flex flex-wrap gap-3">
                        <button
                          type="button"
                          disabled={
                            busyKey ===
                            activeDocument.content_key
                          }
                          onClick={() =>
                            void save(
                              activeDocument,
                            )
                          }
                          className="flex h-11 items-center gap-2 border border-[#314a3b] px-4 text-[9px] font-semibold uppercase tracking-[0.15em]"
                        >
                          <Save size={13} />
                          Save draft
                        </button>

                        <button
                          type="button"
                          disabled={
                            busyKey ===
                            activeDocument.content_key
                          }
                          onClick={() =>
                            void publish(
                              activeDocument,
                            )
                          }
                          className="flex h-11 items-center gap-2 bg-[#314a3b] px-4 text-[9px] font-semibold uppercase tracking-[0.15em] text-[#f3ead7]"
                        >
                          <Send size={13} />
                          Publish
                        </button>
                      </div>
                    </div>

                    <WebsiteDocumentEditor
                      document={
                        activeDocument
                      }
                      onChanged={(
                        changed,
                      ) =>
                        setDocuments(
                          (current) =>
                            current.map(
                              (document) =>
                                document.content_key ===
                                changed.content_key
                                  ? changed
                                  : document,
                            ),
                        )
                      }
                    />
                  </article>
                ),
              )}

              {activeDocuments.length ===
                0 && (
                <div className="border border-[#b5bbae] bg-[#f4efe4] p-8 text-sm text-[#667067]">
                  This page has no editable
                  CMS document loaded.
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    </main>
  );
}
